import { Controller } from '@hotwired/stimulus'

const DENSITIES = ["auto", "compact", "comfortable"]
const COMPACT_COLUMN_WIDTH = 340
const RESERVED_SELECTORS = [".board-rail", ".board-column-new"]

// Owns presentation-only board preferences. Column collapse/focus remains URL
// state in board-columns; this controller only decides how much detail each
// visible card can afford and whether the surrounding app shell is visible.
export default class extends Controller {
  static targets = ["board", "densityOption", "densityLabel", "focusButton", "focusIcon"]
  static values = {
    boardId: String,
    expandedColumns: Number
  }

  connect() {
    this.applyDensity = this.applyDensity.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.applyDensity()
    this.setFocusMode(sessionStorage.getItem(this.focusStorageKey) === "true")
    document.addEventListener("keydown", this.handleKeydown)

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.applyDensity)
      this.resizeObserver.observe(this.boardTarget)
    } else {
      window.addEventListener("resize", this.applyDensity)
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    window.removeEventListener("resize", this.applyDensity)
    document.removeEventListener("keydown", this.handleKeydown)
    document.body.classList.remove("board-focus-mode")
  }

  selectDensity(event) {
    const preference = event.currentTarget.dataset.density
    if (!DENSITIES.includes(preference)) return

    localStorage.setItem(this.densityStorageKey, preference)
    this.applyDensity()
  }

  applyDensity() {
    const preference = this.densityPreference
    this.boardTarget.dataset.boardCardDensity = this.resolvedDensity(preference)
    this.boardTarget.dataset.boardCardDensityPreference = preference

    if (this.hasDensityLabelTarget) {
      this.densityLabelTarget.textContent = this.densityLabel(preference)
    }

    this.densityOptionTargets.forEach((option) => {
      option.setAttribute("aria-pressed", (option.dataset.density === preference).toString())
    })
  }

  toggleFocusMode() {
    const enabled = !document.body.classList.contains("board-focus-mode")
    sessionStorage.setItem(this.focusStorageKey, enabled.toString())
    this.setFocusMode(enabled)
  }

  setFocusMode(enabled) {
    document.body.classList.toggle("board-focus-mode", enabled)
    this.focusButtonTarget.setAttribute("aria-pressed", enabled.toString())
    this.focusButtonTarget.setAttribute("aria-label", enabled ? "Exit board focus mode" : "Enter board focus mode")
    this.focusButtonTarget.dataset.tooltipTextValue = enabled ? "Show navigation" : "Hide navigation and give the board more room"
    this.focusIconTarget.classList.toggle("ti-arrows-maximize", !enabled)
    this.focusIconTarget.classList.toggle("ti-arrows-minimize", enabled)
    requestAnimationFrame(this.applyDensity)
  }

  handleKeydown(event) {
    if (event.key !== "Escape" || !document.body.classList.contains("board-focus-mode")) return

    sessionStorage.setItem(this.focusStorageKey, "false")
    this.setFocusMode(false)
    this.focusButtonTarget.focus()
  }

  resolvedDensity(preference) {
    if (preference !== "auto") return preference
    if (this.boardTarget.dataset.boardDensity === "focus") return "comfortable"

    // The collapsed slivers and the "New list" tile sit in the same row as the
    // open columns, so the room they take is not the columns' to share.
    // Judging the columns on the room they really have means each list you
    // collapse hands its width to the ones left, not just its place in the
    // count.
    const availableWidth = Math.max((this.boardTarget.clientWidth || window.innerWidth) - this.reservedWidth, 0)
    const widthPerColumn = this.expandedColumnsValue > 0
      ? availableWidth / this.expandedColumnsValue
      : availableWidth

    return widthPerColumn < COMPACT_COLUMN_WIDTH ? "compact" : "comfortable"
  }

  get reservedWidth() {
    return RESERVED_SELECTORS.reduce((total, selector) => {
      return total + (this.boardTarget.querySelector(selector)?.offsetWidth || 0)
    }, 0)
  }

  densityLabel(preference) {
    return preference.charAt(0).toUpperCase() + preference.slice(1)
  }

  get densityPreference() {
    const stored = localStorage.getItem(this.densityStorageKey)
    return DENSITIES.includes(stored) ? stored : "auto"
  }

  get densityStorageKey() {
    return `paperwork.board.${this.boardIdValue}.density`
  }

  get focusStorageKey() {
    return `paperwork.board.${this.boardIdValue}.focus`
  }
}
